/**
 * GroupCallPanel.jsx — LiveKit SFU-backed voice call for group
 * conversations (Phase 3 of the LiveKit migration).
 *
 * Rendered by Dashboard when the active "call" is a group call:
 * 1:1 calls continue to use the existing pure-P2P WebRTC modal
 * (zero server media cost, DTLS-bound SAS). Groups — where mesh
 * P2P topology breaks down at N≥3 due to per-peer upload fan-out —
 * route via an E2EE-enforced LiveKit room where the SFU sees
 * only ciphertext frames.
 *
 * ── Flow ────────────────────────────────────────────────────────
 * Initiator:
 *   1. `randomRoomKey()` → 32 bytes of fresh crypto-random material
 *   2. For every peer: RSA-OAEP-wrap those 32 bytes with the peer's
 *      long-term public key → one WS `call_signal` with
 *      `signal_type='livekit_invite'` per peer. Each invite is
 *      sealed for exactly one recipient; nobody else can decrypt.
 *   3. `fetchRoomToken({peerUserIds})` → backend mints a LiveKit
 *      JWT via the Phase 1 endpoint, returns `{url, token, room}`
 *      or `{enabled:false, reason}` for disabled deployments.
 *   4. `buildRoom()` → `connectToRoom({sharedKey})` installs the
 *      32-byte secret BEFORE connecting so the first published
 *      frame is already encrypted.
 *
 * Recipient:
 *   1. WS `livekit_invite` arrives with `{wrapped_key, room_hint}`.
 *      Dashboard surfaces the panel with `isIncoming=true`.
 *   2. User clicks "Join"; `unwrapSharedKey(wrapped_key,
 *      cryptoKeys.privateKey)` → 32 bytes.
 *   3. Same `fetchRoomToken` / `buildRoom` / `connectToRoom` flow.
 *
 * ── SAS verification ────────────────────────────────────────────
 * Both sides derive `deriveSFrameSAS(sharedKey)` — a 6-digit
 * NNN-NNN code binding the spoken audio to the SFrame key. An
 * SFU MITM attempting to swap keys would derive a different code,
 * caught when the humans read the digits aloud. Format matches
 * the DTLS-based SAS from 1:1 calls so the verification UI shape
 * is identical.
 *
 * ── Graceful-disabled posture ───────────────────────────────────
 * If the backend reports `enabled:false` (LIVEKIT_* env vars not
 * set), the panel renders an actionable operator-facing hint
 * ("Set LIVEKIT_URL/KEY/SECRET to enable group calls") instead of
 * a generic failure toast. Matches the pattern used for the
 * warrant-canary Matrix bridge when its env is absent.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import {
  Mic, MicOff, PhoneOff, ShieldCheck, ShieldAlert, Users, Lock,
  CheckCircle2, XCircle, Loader2, ChevronDown, Copy, Check, Hand,
} from 'lucide-react';
import { Button } from '../ui/button';
import { Avatar, AvatarFallback } from '../ui/avatar';
import { useAuth } from '../../context/AuthContext';
import {
  fetchRoomToken, buildRoom, connectToRoom, subscribeRoomEvents,
  enableLocalMicrophone,
} from '../../utils/livekitClient';
import {
  randomRoomKey, deriveSFrameSAS,
} from '../../utils/livekitKeyProvider';
import { wrapSharedKeyForPeer, unwrapSharedKey } from '../../utils/livekitKeyWrap';
import {
  markPeerVerified, recordPeerAttestedMe,
} from '../../utils/voiceSAS';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * localStorage key for the one-time "first group-call verified"
 * celebration. Separate from the 1:1 `ghostcomm_first_sas_celebrated`
 * so both milestones can fire independently — a user who's already
 * done many 1:1 verifications still gets a toast on their first
 * group-call verification, and vice-versa.
 */
const FIRST_GROUP_SAS_CELEBRATED_KEY = 'ghostcomm_first_group_sas_celebrated';

/**
 * RaisedHandsQueue — rendered above ParticipantTiles when ≥2
 * peers (or self + any peer) have a hand up. Tells everyone
 * who's in line and who's first, so the room doesn't dissolve
 * into "everyone unmute at once" when the current speaker
 * finishes. Hidden entirely when 0-1 hands are up — a single
 * raised hand needs no queue context, just the avatar badge
 * already does the job.
 *
 * Order is by raise-time (Map values are timestamps in ms).
 * The "first up" name is special-cased to read "you" if it's
 * the local user, matching the singular-pronoun shape the rest
 * of the panel uses.
 */
const RaisedHandsQueue = ({ raisedHandIds, localUserId, peers, onTakeFloor }) => {
  if (!raisedHandIds || raisedHandIds.size < 2) return null;
  const ordered = Array.from(raisedHandIds.entries())
    .sort((a, b) => a[1] - b[1])
    .map(([id]) => id);
  const firstId = ordered[0];
  const localIsFirst = firstId === localUserId;
  const firstName = localIsFirst
    ? 'you'
    : (peers || []).find((p) => p?.id === firstId)?.username || 'someone';
  return (
    <div
      className="flex items-center justify-center gap-2 px-3 py-2 rounded-full bg-amber-500/10 border border-amber-500/30 text-[11px] text-amber-200"
      data-testid="group-call-hand-queue"
      data-queue-size={raisedHandIds.size}
      data-local-is-first={localIsFirst ? 'true' : 'false'}
    >
      <Hand className="w-3 h-3 shrink-0" />
      <span>
        <strong className="font-semibold text-amber-100">{raisedHandIds.size} in queue</strong>
        <span className="text-amber-200/70"> · </span>
        <span>{firstName} first</span>
      </span>
      {localIsFirst && (
        <button
          type="button"
          onClick={onTakeFloor}
          className="ml-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-500 hover:bg-amber-400 text-amber-950 font-semibold text-[10px] uppercase tracking-wider transition-colors"
          data-testid="group-call-take-floor-btn"
          title="Unmute, lower your hand, and take the floor"
        >
          Take the floor
        </button>
      )}
    </div>
  );
};


/**
 * ParticipantTiles — replaces the "In room · N" count pill with a
 * row of small circular avatars, one per call member, with a
 * pulsing emerald ring around any participant who is currently
 * above LiveKit's server-side speaker-detection threshold. Same
 * "see who's talking" affordance Discord and Zoom built their
 * group-call UX around — no audio leaks, just an aggregate
 * speaking-state derived from the same SFrame-encrypted stream.
 *
 * Props:
 *   localUser           the AuthContext user object (id, username)
 *   localIsSpeaking     boolean — is `localUser` an active speaker?
 *   localIsMuted        boolean — render a muted overlay if so
 *   peers               array of { id, username, public_key } from
 *                       props (resolved by Dashboard.handleGroupCall)
 *   connectedIds        identities currently joined to the SFU,
 *                       from RoomEvent.ParticipantConnected/-Disconnected
 *   activeSpeakerIds    Set<string> from RoomEvent.ActiveSpeakersChanged
 *
 * The tile state machine: every tile is dimmed unless its
 * participant is in `connectedIds`. Connected + speaking gets
 * the emerald ring; connected + silent gets a flat avatar;
 * not-yet-joined gets a slate-grey "joining…" tile.
 */
const ParticipantTiles = ({
  localUser, localIsSpeaking, localIsMuted, peers,
  connectedIds, activeSpeakerIds, raisedHandIds,
}) => {
  // Build the canonical roster: local user first, then peers in
  // the order Dashboard provided (which matches the conversation
  // participant order — stable across re-renders).
  const roster = [
    { id: localUser?.id, username: localUser?.username, isLocal: true },
    ...(peers || []).filter((p) => p?.id && p.id !== localUser?.id),
  ];
  const connectedSet = new Set(connectedIds || []);

  return (
    <div
      className="flex flex-wrap items-start justify-center gap-3 px-1"
      data-testid="group-call-participants"
    >
      {roster.map((m) => {
        if (!m?.id) return null;
        const joined = m.isLocal || connectedSet.has(m.id);
        const speaking = m.isLocal
          ? Boolean(localIsSpeaking)
          : (activeSpeakerIds && activeSpeakerIds.has(m.id));
        const muted = m.isLocal && localIsMuted;
        const initials = (m.username || '?')
          .split(/\s+/)
          .map((s) => s.charAt(0).toUpperCase())
          .slice(0, 2)
          .join('');

        return (
          <div
            key={m.id}
            className="flex flex-col items-center gap-1.5 min-w-[64px]"
            data-testid={`group-call-participant-tile-${m.id}`}
            data-joined={joined ? 'true' : 'false'}
            data-speaking={speaking ? 'true' : 'false'}
          >
            <div className="relative">
              {/* Pulsing speaking-ring. Sits OUTSIDE the avatar
                  so it can scale without affecting layout. The
                  emerald glow uses ring-* utilities so it's
                  GPU-cheap on weaker mobile hardware. */}
              <span
                className={`absolute inset-0 rounded-full transition-all duration-150 pointer-events-none ${
                  speaking
                    ? 'ring-2 ring-emerald-400 ring-offset-2 ring-offset-card animate-pulse'
                    : ''
                }`}
                aria-hidden="true"
              />
              <div
                className={`h-12 w-12 rounded-full flex items-center justify-center text-sm font-semibold border-2 ${
                  joined
                    ? 'bg-primary/20 text-primary border-primary/30'
                    : 'bg-slate-800/50 text-slate-500 border-slate-700/50'
                }`}
              >
                {initials}
              </div>
              {muted && (
                <span
                  className="absolute -bottom-1 -right-1 inline-flex items-center justify-center h-5 w-5 rounded-full bg-red-500/90 border border-card text-white"
                  data-testid={`group-call-tile-mute-icon-${m.id}`}
                  title="Muted"
                >
                  <MicOff className="w-2.5 h-2.5" />
                </span>
              )}
              {raisedHandIds && raisedHandIds.has(m.id) && (
                <span
                  className="absolute -top-1 -right-1 inline-flex items-center justify-center h-5 w-5 rounded-full bg-amber-500/90 border border-card text-white animate-bounce"
                  data-testid={`group-call-tile-hand-raised-${m.id}`}
                  title={m.isLocal ? 'You raised your hand' : `${m.username || 'peer'} raised a hand`}
                >
                  <Hand className="w-2.5 h-2.5" />
                </span>
              )}
            </div>
            <span className="text-[10px] text-slate-400 max-w-[80px] truncate">
              {m.isLocal ? 'You' : (m.username || 'peer')}
            </span>
            {!joined && !m.isLocal && (
              <span className="text-[9px] text-slate-600 uppercase tracking-wider">
                joining…
              </span>
            )}
          </div>
        );
      })}
    </div>
  );
};


/**
 * `invitePayload` (when isIncoming): { fromUserId, wrappedKey }
 * `peers` always: array of peer user-objects { id, username,
 *                 public_key? } excluding the local user.
 */
export const GroupCallPanel = ({
  isOpen,
  onClose,
  conversation,
  peers,
  isIncoming,
  invitePayload,
}) => {
  const { sendWsMessage, user, cryptoKeys, addWsListener } = useAuth();

  /**
   * State machine:
   *   'incoming'   — Recipient has received invite, waiting for
   *                  them to click Join.
   *   'fetching'   — Fetching token from backend.
   *   'disabled'   — Backend reports LiveKit is not configured.
   *                  Terminal; user closes the panel.
   *   'connecting' — Token obtained, connecting to SFU.
   *   'connected'  — In the room, audio flowing.
   *   'error'      — Any fatal error; show reason and Close.
   */
  const [phase, setPhase] = useState(isIncoming ? 'incoming' : 'fetching');
  const [reason, setReason] = useState(null);
  const [sasCode, setSasCode] = useState(null);
  const [sasState, setSasState] = useState('pending');
  const [isMuted, setIsMuted] = useState(false);
  const [participantIds, setParticipantIds] = useState([]);
  // Identity-set of participants currently above LiveKit's
  // server-side speaker-detection threshold. Updated by the
  // RoomEvent.ActiveSpeakersChanged handler (10 Hz on the wire).
  // Stored as a Set for O(1) membership checks during render —
  // the avatar tile array is in the render path so it's worth
  // dodging the .includes() scan on every re-render.
  const [activeSpeakers, setActiveSpeakers] = useState(() => new Set());
  // Map<participantId, raisedAtMs> — replaces a plain Set so we
  // can render an ordered "raised hands queue" strip when 2+
  // people are signalling at once. `.has(id)` works on Map the
  // same way it does on Set, so the existing ParticipantTiles
  // membership-check render path stays a one-line change.
  const [raisedHandIds, setRaisedHandIds] = useState(() => new Map());
  // Expansion state + copy-feedback state for the "Show me how"
  // deploy helper on the disabled phase. Default collapsed so the
  // 95% of users who aren't self-hosting don't see a wall of
  // operator config. `copied` briefly flips the Copy icon to a
  // Check for ~1.5s after a successful clipboard write.
  const [deployExpanded, setDeployExpanded] = useState(false);
  const [copied, setCopied] = useState(false);

  // Refs survive unmounts; state triggers re-renders.
  const roomRef = useRef(null);
  const providerRef = useRef(null);
  const workerRef = useRef(null);
  const unsubRef = useRef(null);
  const sharedKeyRef = useRef(null); // Uint8Array(32) — sensitive.

  const peerIds = useMemo(
    () => (peers || []).map((p) => p.id).filter((pid) => pid && pid !== user?.id),
    [peers, user],
  );

  /**
   * Dispose LiveKit state. Idempotent; safe to call on every
   * close path. The 32-byte sharedKey is zeroed before release —
   * a LiveKit room disconnect alone would otherwise leave it
   * sitting in JS heap until the next GC cycle.
   */
  const teardown = useCallback(() => {
    if (unsubRef.current) { try { unsubRef.current(); } catch {} unsubRef.current = null; }
    if (roomRef.current) { try { roomRef.current.disconnect(); } catch {} roomRef.current = null; }
    if (workerRef.current) { try { workerRef.current.terminate(); } catch {} workerRef.current = null; }
    providerRef.current = null;
    if (sharedKeyRef.current) {
      try { sharedKeyRef.current.fill(0); } catch {}
      sharedKeyRef.current = null;
    }
  }, []);

  /**
   * Get (or derive) the 32-byte shared secret.
   *
   * Outgoing call: generate fresh. Also RSA-OAEP-wrap per peer
   * and fire one `call_signal` with `signal_type='livekit_invite'`
   * per peer. Peers without a resolvable public_key are skipped
   * (with a UI toast) rather than silently dropped.
   *
   * Incoming call: unwrap the invitePayload.wrappedKey.
   */
  const _obtainSharedKey = useCallback(async () => {
    if (isIncoming) {
      if (!invitePayload?.wrappedKey) {
        throw new Error('Invite payload missing wrapped key');
      }
      if (!cryptoKeys?.privateKey) {
        throw new Error('Your private key is not loaded');
      }
      return unwrapSharedKey(invitePayload.wrappedKey, cryptoKeys.privateKey);
    }

    const key = randomRoomKey();
    let deliveredTo = 0;
    let skipped = 0;
    for (const peer of peers || []) {
      if (!peer?.id || peer.id === user.id) continue;
      if (!peer.public_key) {
        // Peer hasn't published a public key yet — can't invite.
        // UI surfaces a count rather than listing names so the
        // panel doesn't turn into an error dump.
        skipped += 1;
        continue;
      }
      try {
        const wrapped = await wrapSharedKeyForPeer(key, peer.public_key);
        sendWsMessage({
          type: 'call_signal',
          target_user_id: peer.id,
          signal_type: 'livekit_invite',
          signal_data: {
            conversation_id: conversation?.id,
            wrapped_key: wrapped,
            // `initiator_id` is redundant with `from_user_id` on
            // the server side but including it defends against
            // a future WS-relay change that might drop the
            // implicit sender metadata.
            initiator_id: user.id,
          },
        });
        deliveredTo += 1;
      } catch (e) {
        skipped += 1;
      }
    }
    if (skipped > 0) {
      toast.info(`Invite sent to ${deliveredTo} peer${deliveredTo === 1 ? '' : 's'}; ${skipped} skipped (missing key).`);
    }
    return key;
  }, [isIncoming, invitePayload, cryptoKeys, peers, user, conversation, sendWsMessage]);

  /**
   * Join the room: obtain shared key → mint token → build room →
   * install key → connect. Sets phase accordingly. On any error,
   * lands on 'error' with a readable reason instead of throwing
   * into a void.
   */
  const _join = useCallback(async () => {
    try {
      setPhase('fetching');

      // Resolve peers list BEFORE fetching token so we can pass
      // explicit peer IDs (backend does the mutual-contact check).
      const targetPeerIds = isIncoming
        ? [invitePayload.fromUserId].concat(peerIds.filter((pid) => pid !== invitePayload.fromUserId))
        : peerIds;

      if (targetPeerIds.length === 0) {
        setPhase('error');
        setReason('This conversation has no other participants to call.');
        return;
      }

      const authToken = localStorage.getItem('ghostcomm_token');
      const tokenRes = await fetchRoomToken({
        peerUserIds: targetPeerIds,
        authToken,
      });

      if (!tokenRes?.enabled) {
        setPhase('disabled');
        setReason(tokenRes?.reason || 'Group calls are not configured on this deployment.');
        return;
      }

      // Shared key flows: initiator generates & sends invites;
      // recipient unwraps the incoming invite.
      const sharedKey = await _obtainSharedKey();
      sharedKeyRef.current = sharedKey;

      // SAS code is deterministic on the 32 bytes alone, so it's
      // available before we connect — surface it early so the
      // humans can start comparing while the SFU handshakes.
      const sas = await deriveSFrameSAS(sharedKey);
      setSasCode(sas || 'unsupported');

      setPhase('connecting');

      const { room, provider, worker } = buildRoom();
      roomRef.current = room;
      providerRef.current = provider;
      workerRef.current = worker;

      unsubRef.current = subscribeRoomEvents(room, {
        onParticipantConnected: (p) => {
          setParticipantIds((prev) => prev.includes(p.identity) ? prev : [...prev, p.identity]);
        },
        onParticipantDisconnected: (p) => {
          setParticipantIds((prev) => prev.filter((pid) => pid !== p.identity));
          // Drop the participant from the active-speaker set on
          // disconnect — LiveKit doesn't always fire a
          // speakers-changed when the speaker LEAVES, so the
          // ring would otherwise persist on a ghost tile until
          // the next genuine speaker change.
          setActiveSpeakers((prev) => {
            if (!prev.has(p.identity)) return prev;
            const next = new Set(prev);
            next.delete(p.identity);
            return next;
          });
          // Same for hand-raise: drop the badge if a participant
          // disconnects with their hand still up. Without this
          // the badge would persist on a ghost tile until they
          // reconnect and explicitly lower.
          setRaisedHandIds((prev) => {
            if (!prev.has(p.identity)) return prev;
            const next = new Map(prev);
            next.delete(p.identity);
            return next;
          });
        },
        onDisconnected: () => {
          setPhase('error');
          setReason('Disconnected from the SFU.');
        },
        onE2EEError: (err) => {
          setPhase('error');
          setReason(`E2EE error: ${err?.message || 'unknown'}`);
        },
        onActiveSpeakersChanged: (speakers) => {
          // `speakers` is the FULL set of currently-speaking
          // participants (both local + remote), not a delta —
          // wholesale replace is correct and cheaper than a
          // diff. Empty array means silence.
          const ids = (speakers || []).map((p) => p?.identity).filter(Boolean);
          setActiveSpeakers(new Set(ids));
        },
      });

      await connectToRoom({
        room,
        provider,
        url: tokenRes.url,
        token: tokenRes.token,
        sharedKey,
      });

      await enableLocalMicrophone(room);
      setPhase('connected');

      // Seed participantIds with anyone who was already in the
      // room when we arrived. RemoteParticipants is iterable.
      const seed = [];
      room.remoteParticipants?.forEach?.((p) => { if (p?.identity) seed.push(p.identity); });
      if (seed.length) setParticipantIds(seed);
    } catch (e) {
      console.error('[GroupCallPanel] _join failed:', e);
      setPhase('error');
      setReason(e?.message || String(e) || 'Failed to join group call');
      teardown();
    }
  }, [isIncoming, invitePayload, peerIds, _obtainSharedKey, teardown]);

  // Kick off auto-join for outgoing calls; incoming calls wait
  // for the user to click "Join".
  useEffect(() => {
    if (!isOpen) return;
    if (!isIncoming) {
      _join();
    }
    return () => teardown();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, isIncoming]);

  /**
   * Listen for `call_signal:sas_attest` frames from the OTHER
   * participants in this group call. When they click "Codes
   * match" on their side, we record a "they verified me"
   * tombstone so mutual-verification flows into the halo
   * counter. Mirrors VoiceCallModal's identical handler.
   *
   * Scoped to the panel lifetime — unsubscribes automatically
   * on unmount, so we don't process attestations from unrelated
   * future calls. Peer IDs are vetted against the current
   * `peers` list so a malicious / out-of-band WS sender can't
   * inflate someone's mutual-verification count from the outside.
   */
  useEffect(() => {
    if (!isOpen || !addWsListener) return undefined;
    const peerIdSet = new Set((peers || []).map((p) => p?.id).filter(Boolean));
    const unsub = addWsListener((data) => {
      if (
        data?.type !== 'call_signal' ||
        !data?.from_user_id ||
        !peerIdSet.has(data.from_user_id)
      ) return;
      // SAS attestation — mirrors VoiceCallModal.
      if (data.signal_type === 'sas_attest') {
        recordPeerAttestedMe(data.from_user_id, data.signal_data?.sas);
        return;
      }
      // Hand raise / lower from a remote peer. Non-bool `raised`
      // values default to true so older client versions that
      // don't send the explicit `{raised: bool}` (i.e. just a
      // gesture frame) still produce the expected raise.
      if (data.signal_type === 'hand_raise') {
        const raising = data.signal_data?.raised !== false;
        setRaisedHandIds((prev) => {
          const next = new Map(prev);
          if (raising) next.set(data.from_user_id, Date.now());
          else next.delete(data.from_user_id);
          return next;
        });
        return;
      }
    });
    return unsub;
  }, [isOpen, addWsListener, peers]);

  const handleAnswer = () => { _join(); };

  const handleHangup = () => {
    teardown();
    onClose();
  };

  const handleToggleMute = async () => {
    const room = roomRef.current;
    if (!room?.localParticipant) return;
    const next = !isMuted;
    try {
      await room.localParticipant.setMicrophoneEnabled(!next);
      setIsMuted(next);
    } catch {
      toast.error('Failed to toggle microphone');
    }
  };

  /**
   * Toggle the local user's hand-raise state. Updates local
   * state immediately for responsiveness, then broadcasts a
   * `call_signal:hand_raise` WS frame to every peer in the
   * room with `{raised: bool}`. We send to ALL peers (not just
   * those currently connected to the SFU) so a participant
   * who joined late catches up on existing raises via the
   * WS-relayed event log — same broadcast pattern the
   * `sas_attest` flow already uses. Send failures are non-
   * fatal: the local UI flips regardless so the user has
   * unambiguous feedback that they performed the gesture.
   */
  const handleToggleRaiseHand = useCallback(() => {
    const myId = user?.id;
    if (!myId) return;
    // Compute the new state SYNCHRONOUSLY from the closure-
    // captured `raisedHandIds` rather than relying on the
    // functional setter's callback to run before we broadcast.
    // React 18+ batches setState in event handlers, which
    // means a `let raisingNow=false; setRaisedHandIds(prev =>
    // { ...; raisingNow=true; })` pattern broadcasts the WS
    // frame BEFORE the callback executes — peers receive
    // `raised:false` on every click. Computing from the
    // closure value sidesteps the timing problem entirely.
    // Safe because the setter that runs next replaces the
    // state with our exact computed Set, so no race window
    // exists where two clicks could double-toggle.
    const raisingNow = !raisedHandIds.has(myId);
    setRaisedHandIds((prev) => {
      const next = new Map(prev);
      if (raisingNow) next.set(myId, Date.now());
      else next.delete(myId);
      return next;
    });
    (peers || []).forEach((peer) => {
      if (!peer?.id || peer.id === myId) return;
      try {
        sendWsMessage({
          type: 'call_signal',
          target_user_id: peer.id,
          signal_type: 'hand_raise',
          signal_data: { raised: raisingNow },
        });
      } catch {
        /* non-fatal */
      }
    });
  }, [user, peers, sendWsMessage, raisedHandIds]);

  /**
   * "Take the floor" — convenience action available to whoever
   * is currently first-up in the raised-hand queue. Combines:
   *   - unmute the local mic (room.localParticipant.setMic +
   *     local isMuted state)
   *   - lower the local hand (drops the badge on every peer's
   *     panel + dequeues us)
   * Idempotent: if you're already unmuted, the unmute path
   * just no-ops; if your hand is already lowered, ditto.
   * Failure to unmute (browser permissions revoked mid-call)
   * is non-fatal — we still lower the hand so the queue moves
   * on cleanly without you stuck at the head.
   */
  const handleTakeFloor = useCallback(async () => {
    const room = roomRef.current;
    if (room?.localParticipant) {
      try {
        await room.localParticipant.setMicrophoneEnabled(true);
        setIsMuted(false);
      } catch {
        toast.error('Mic unmute failed — others may not hear you');
      }
    }
    // Lower the hand if it's still up. Re-uses the existing
    // toggle handler so the WS broadcast + state-update path
    // stays in one place.
    if (user?.id && raisedHandIds.has(user.id)) {
      handleToggleRaiseHand();
    }
  }, [user, raisedHandIds, handleToggleRaiseHand]);

  const handleSasMatch = () => {
    setSasState('match');
    // Record a per-peer "you verified them" tombstone for every
    // peer in the group, so the Security Halo card's "voice-
    // verified" tile counts group calls identically to 1:1s.
    // This mirrors VoiceCallModal.handleSasMatch — the ledger
    // primitives are intentionally unaware of call topology.
    (peers || []).forEach((peer) => {
      if (!peer?.id || peer.id === user?.id) return;
      markPeerVerified(peer.id, sasCode, peer.username);
      // Tell the peer we attested, reusing the exact same
      // `call_signal:sas_attest` frame shape 1:1 calls use. The
      // recipient's GroupCallPanel WS listener (wired below)
      // picks this up and writes its own "they verified me"
      // tombstone — producing the mutual-verification count
      // that the halo calls out as the lived-in north star.
      try {
        sendWsMessage({
          type: 'call_signal',
          target_user_id: peer.id,
          signal_type: 'sas_attest',
          signal_data: { sas: sasCode },
        });
      } catch {
        /* non-fatal — local tombstone already written */
      }
    });

    // First-ever group-call verification celebration. Fires once
    // per device (guarded by the localStorage flag). Separate
    // flag from the 1:1 version so both milestones can fire
    // independently. Skip in duress — a coerced session must
    // never signal "you just levelled up" because the coercer
    // must not realise the account has a working trust-
    // verification ritual at all. Mirrors VoiceCallModal's
    // first-voice-verified toast shape, including the "See halo"
    // deep-link action.
    let celebrated = false;
    try {
      const alreadyCelebrated = localStorage.getItem(FIRST_GROUP_SAS_CELEBRATED_KEY);
      const inDuress = sessionStorage.getItem('gs_duress_mode') === '1';
      if (!alreadyCelebrated && !inDuress) {
        localStorage.setItem(FIRST_GROUP_SAS_CELEBRATED_KEY, String(Date.now()));
        celebrated = true;
      }
    } catch {
      /* storage disabled — fall back to standard toast */
    }
    if (celebrated) {
      toast.success('First group call verified — your trust graph is branching out 🕸️', {
        duration: 6000,
        action: {
          label: 'See halo',
          onClick: () => {
            window.dispatchEvent(new CustomEvent('gs:open-security-halo'));
          },
        },
      });
    } else {
      toast.success('Group call verified');
    }
  };
  const handleSasMismatch = () => {
    setSasState('mismatch');
    toast.error('Codes do not match — leave the call immediately');
  };

  /**
   * Pre-filled .env + `docker compose` snippet for the Deploy-LiveKit
   * helper. Pure function of nothing (fully static) so we can
   * define it at module-scope below the component. Inline here to
   * keep the deploy block self-contained when someone reads this
   * file top-to-bottom.
   *
   * The `LIVEKIT_API_SECRET` placeholder uses a clearly-labelled
   * "replace-me" token rather than a real base64 blob so an admin
   * who copy-pastes-and-forgets doesn't ship production with the
   * example secret.
   */
  const DEPLOY_ENV_BLOCK = `# LiveKit SFU — voice-call relay for 3+ participants
# Generate a 32-byte secret: openssl rand -base64 32
LIVEKIT_URL=wss://voice.yourdomain.com
LIVEKIT_API_KEY=APIgovshield${Math.floor(Math.random() * 1e8).toString(36)}
LIVEKIT_API_SECRET=REPLACE_WITH_32_RANDOM_BYTES_BASE64_ENCODED`;

  const DEPLOY_COMPOSE_BLOCK = `# 1. Edit selfhost/livekit-config.yaml (copy from the .example file)
# 2. Uncomment the livekit service block in selfhost/docker-compose.yml
# 3. Reverse-proxy wss://voice.yourdomain.com → livekit:7880
# 4. Start the stack:
cd selfhost && docker compose up -d livekit
# 5. Restart the Government Shield backend to pick up the new env:
docker compose restart backend`;

  const handleCopyDeployBlock = async () => {
    const full = `${DEPLOY_ENV_BLOCK}\n\n${DEPLOY_COMPOSE_BLOCK}`;
    try {
      await navigator.clipboard.writeText(full);
      setCopied(true);
      toast.success('Deploy block copied to clipboard');
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error('Clipboard blocked — select the text manually');
    }
  };

  if (!isOpen) return null;

  const groupName = conversation?.encrypted_name || 'Group call';

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm"
      data-testid="group-call-panel"
      data-phase={phase}
    >
      <div className="bg-card p-8 rounded-2xl border border-border text-center max-w-md w-full mx-4">
        <div className="relative inline-block mb-4">
          <Avatar className="h-20 w-20">
            <AvatarFallback className="bg-primary/20 text-primary text-xl">
              <Users className="w-10 h-10" />
            </AvatarFallback>
          </Avatar>
        </div>

        <h3 className="text-xl font-semibold text-white mb-1" data-testid="group-call-title">
          {groupName}
        </h3>
        <p className="text-xs text-slate-400 mb-5 uppercase tracking-[0.18em]">
          {peers?.length ?? 0} invited · LiveKit SFU
        </p>

        {/* Phase-specific body */}
        {phase === 'incoming' && (
          <div className="space-y-4">
            <p className="text-sm text-slate-300">
              Incoming group call from <span className="text-white font-medium">
                {peers?.find((p) => p.id === invitePayload?.fromUserId)?.username || 'a contact'}
              </span>.
            </p>
            <div className="flex items-center justify-center gap-4">
              <Button
                onClick={handleAnswer}
                className="h-14 w-14 rounded-full bg-emerald-600 hover:bg-emerald-700"
                data-testid="group-call-answer-btn"
              >
                <Mic className="w-6 h-6" />
              </Button>
              <Button
                onClick={handleHangup}
                className="h-14 w-14 rounded-full bg-red-600 hover:bg-red-700"
                data-testid="group-call-decline-btn"
              >
                <PhoneOff className="w-6 h-6" />
              </Button>
            </div>
          </div>
        )}

        {(phase === 'fetching' || phase === 'connecting') && (
          <div className="flex flex-col items-center gap-3 py-4">
            <Loader2 className="w-6 h-6 text-primary animate-spin" />
            <p className="text-sm text-slate-400">
              {phase === 'fetching' ? 'Minting room token…' : 'Negotiating encrypted room…'}
            </p>
          </div>
        )}

        {phase === 'disabled' && (
          <div
            className="space-y-3 text-left"
            data-testid="group-call-disabled"
          >
            <div className="p-4 rounded-lg bg-amber-500/10 border border-amber-500/30 text-amber-200 text-sm leading-relaxed">
              <p className="font-medium mb-1">Group calls not configured</p>
              <p className="text-xs text-amber-100/80">
                {reason}
              </p>
            </div>
            <p className="text-xs text-slate-500">
              1:1 voice calls continue to work on pure P2P WebRTC. The
              LiveKit SFU path unlocks at 3+ participants once the
              operator sets <code className="font-mono text-[10px] text-slate-400">LIVEKIT_URL</code>,{' '}
              <code className="font-mono text-[10px] text-slate-400">LIVEKIT_API_KEY</code>,{' '}
              <code className="font-mono text-[10px] text-slate-400">LIVEKIT_API_SECRET</code>.
            </p>

            {/* "Show me how" — zero-friction deploy helper for
                self-hosting operators. Collapsed by default
                (most end-users don't run the server). Expands to
                reveal a copy-paste .env + docker compose snippet
                with a one-click clipboard action. Same pattern
                Plausible/Umami use to turn "disabled-integration"
                screens into activation paths rather than dead
                ends. */}
            <div
              className="rounded-lg border border-slate-700/50 bg-slate-900/40 overflow-hidden"
              data-testid="group-call-deploy-helper"
            >
              <button
                type="button"
                onClick={() => setDeployExpanded((v) => !v)}
                className="w-full flex items-center justify-between px-3 py-2.5 text-xs text-slate-300 hover:bg-slate-800/40 transition-colors"
                data-testid="group-call-deploy-toggle"
                data-expanded={deployExpanded ? 'true' : 'false'}
              >
                <span className="flex items-center gap-2">
                  <span className="font-medium">Self-hosting? Show me how.</span>
                </span>
                <ChevronDown
                  className={`w-4 h-4 text-slate-500 transition-transform ${deployExpanded ? 'rotate-180' : ''}`}
                />
              </button>
              {deployExpanded && (
                <div className="border-t border-slate-700/50 p-3 space-y-3" data-testid="group-call-deploy-body">
                  <p className="text-[11px] text-slate-400 leading-relaxed">
                    Paste into your <code className="font-mono text-slate-300">selfhost/.env</code>, run
                    the compose block below, and restart the backend. The SFU
                    is Apache-2.0, self-contained, and ~40 MB idle.
                  </p>
                  <pre
                    className="font-mono text-[10px] leading-[1.5] text-slate-300 bg-black/60 border border-slate-800 rounded p-2.5 overflow-x-auto whitespace-pre"
                    data-testid="group-call-deploy-env"
                  >
{DEPLOY_ENV_BLOCK}
                  </pre>
                  <pre
                    className="font-mono text-[10px] leading-[1.5] text-slate-300 bg-black/60 border border-slate-800 rounded p-2.5 overflow-x-auto whitespace-pre"
                    data-testid="group-call-deploy-compose"
                  >
{DEPLOY_COMPOSE_BLOCK}
                  </pre>
                  <Button
                    onClick={handleCopyDeployBlock}
                    variant="outline"
                    className="w-full h-9 text-xs"
                    data-testid="group-call-deploy-copy-btn"
                  >
                    {copied ? (
                      <>
                        <Check className="w-3.5 h-3.5 mr-1.5 text-emerald-400" />
                        Copied
                      </>
                    ) : (
                      <>
                        <Copy className="w-3.5 h-3.5 mr-1.5" />
                        Copy deploy block
                      </>
                    )}
                  </Button>
                  <p className="text-[10px] text-slate-500 leading-relaxed">
                    See <code className="font-mono text-slate-400">selfhost/livekit-config.yaml.example</code> for
                    the full server config (egress disabled, ingress disabled,
                    E2EE-required — GS defaults).
                  </p>
                </div>
              )}
            </div>

            <Button
              onClick={handleHangup}
              variant="outline"
              className="w-full"
              data-testid="group-call-close-btn"
            >
              Close
            </Button>
          </div>
        )}

        {phase === 'error' && (
          <div className="space-y-3 text-left" data-testid="group-call-error">
            <div className="p-4 rounded-lg bg-rose-500/10 border border-rose-500/30 text-rose-200 text-sm">
              <p className="font-medium mb-1">Couldn't start the call</p>
              <p className="text-xs text-rose-100/80">{reason}</p>
            </div>
            <Button onClick={handleHangup} variant="outline" className="w-full">
              Close
            </Button>
          </div>
        )}

        {phase === 'connected' && (
          <div className="space-y-5">
            <RaisedHandsQueue
              raisedHandIds={raisedHandIds}
              localUserId={user?.id}
              peers={peers}
              onTakeFloor={handleTakeFloor}
            />

            <ParticipantTiles
              localUser={user}
              localIsSpeaking={user?.id ? activeSpeakers.has(user.id) : false}
              localIsMuted={isMuted}
              peers={peers}
              connectedIds={participantIds}
              activeSpeakerIds={activeSpeakers}
              raisedHandIds={raisedHandIds}
            />

            <div className="flex items-center justify-center gap-4">
              <Button
                variant="secondary"
                onClick={handleToggleMute}
                className={`h-14 w-14 rounded-full ${isMuted ? 'bg-red-600 hover:bg-red-700' : ''}`}
                data-testid="group-call-mute-btn"
              >
                {isMuted ? <MicOff className="w-6 h-6" /> : <Mic className="w-6 h-6" />}
              </Button>
              <Button
                variant="secondary"
                onClick={handleToggleRaiseHand}
                className={`h-14 w-14 rounded-full ${
                  user?.id && raisedHandIds.has(user.id)
                    ? 'bg-amber-500 hover:bg-amber-600 text-white'
                    : ''
                }`}
                data-testid="group-call-raise-hand-btn"
                data-hand-raised={user?.id && raisedHandIds.has(user.id) ? 'true' : 'false'}
                title={user?.id && raisedHandIds.has(user.id) ? 'Lower your hand' : 'Raise your hand'}
                aria-label={user?.id && raisedHandIds.has(user.id) ? 'Lower your hand' : 'Raise your hand'}
              >
                <Hand className="w-6 h-6" />
              </Button>
              <Button
                onClick={handleHangup}
                className="h-14 w-14 rounded-full bg-red-600 hover:bg-red-700"
                data-testid="group-call-end-btn"
              >
                <PhoneOff className="w-6 h-6" />
              </Button>
            </div>

            <div className="mt-5 pt-5 border-t border-border/50 space-y-2">
              <div className="flex items-center justify-center gap-2 text-xs text-slate-500">
                <Lock className="w-3 h-3" />
                <span>End-to-end encrypted · SFrame via LiveKit SFU</span>
              </div>
            </div>
          </div>
        )}

        {/* SAS verification block — rendered during `connecting`,
            `connected`, AND `error` phases. Deriving the SAS
            before the SFU handshake completes lets the two
            humans start comparing codes immediately, which (a)
            surfaces a MITM the moment the invite round-trip
            finishes (no need to wait for media), and (b) keeps
            the verification signal visible even if the SFU
            connection fails — users still know whether the
            key-distribution layer was untampered. */}
        {(phase === 'connecting' || phase === 'connected' || phase === 'error') && sasCode && (
          <div
            className="mt-5 pt-5 border-t border-border/50"
            data-testid="group-sas-verification-block"
            data-sas-state={sasState}
            data-sas-code={sasCode}
          >
            {sasCode === 'unsupported' ? (
              <p className="text-xs text-slate-500 text-center">
                Voice verification unavailable for this room.
              </p>
            ) : sasState === 'match' ? (
              <div
                className="flex items-center justify-center gap-2 text-sm text-emerald-400"
                data-testid="group-sas-verified-badge"
              >
                <ShieldCheck className="w-4 h-4" />
                <span>Room verified · <span className="font-mono">{sasCode}</span></span>
              </div>
            ) : sasState === 'mismatch' ? (
              <div className="space-y-3" data-testid="group-sas-mismatch-warning">
                <div className="flex items-start gap-2 px-3 py-2.5 rounded-lg bg-rose-500/10 border border-rose-500/30 text-rose-300 text-xs leading-relaxed">
                  <ShieldAlert className="w-4 h-4 shrink-0 mt-0.5" />
                  <span>
                    <strong className="text-rose-200">Codes do not match.</strong>{' '}
                    The SFU may be substituting keys. Leave the room and re-invite through another channel.
                  </span>
                </div>
                <Button onClick={handleHangup} variant="destructive" className="w-full h-10">
                  Leave room now
                </Button>
              </div>
            ) : (
              <div className="space-y-3">
                <p className="text-[10px] uppercase tracking-[0.18em] text-slate-500 text-center">
                  Say this code aloud to verify the room
                </p>
                <div
                  className="font-mono text-3xl tracking-[0.25em] text-center text-primary select-all"
                  data-testid="group-sas-code-display"
                >
                  {sasCode}
                </div>
                <p className="text-xs text-slate-400 text-center leading-relaxed">
                  Every participant should see the same code. Any mismatch means an SFrame-key swap is in play.
                </p>
                <div className="flex gap-2">
                  <Button
                    onClick={handleSasMatch}
                    className="flex-1 h-9 bg-emerald-600 hover:bg-emerald-700 text-white"
                    data-testid="group-sas-match-btn"
                  >
                    <CheckCircle2 className="w-4 h-4 mr-1.5" /> Codes match
                  </Button>
                  <Button
                    onClick={handleSasMismatch}
                    variant="outline"
                    className="flex-1 h-9 border-rose-500/40 text-rose-300 hover:bg-rose-500/10"
                    data-testid="group-sas-mismatch-btn"
                  >
                    <XCircle className="w-4 h-4 mr-1.5" /> Don't match
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}

      </div>
    </div>
  );
};

export default GroupCallPanel;
